import { Chip } from 'primereact/chip';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { MdOutlineCreate } from 'react-icons/md';
import styles from './payment-method-table.module.css';
import { TextTableHeader } from '@/components/text/Texts';
import { showTypedModal2, GenericResponse } from '@/common/common_utils';
import RequestEditPaymentFormDialog from '../../billing-information/RequestEditPaymentFormDialog';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import type { PaymentCardDto } from '../AddPaymentModal';
import moment from 'moment';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

interface PaymentMethod {
	id: number;
	name: string;
	paymentMethod: string;
	expiration: string;
	lastUsed: string;
	isDefault?: boolean;
}

const columns: { field: keyof PaymentMethod | 'action'; header: string }[] = [
	{ field: 'name', header: 'Name' },
	{ field: 'paymentMethod', header: 'Payment Methods' },
	{ field: 'expiration', header: 'Expiration' },
	{ field: 'lastUsed', header: 'Last Used' },
	{ field: 'action', header: 'Action' },
];

const PaymentMethodTable = () => {
	const hasRoles = useAuthenticationStore((state) => state.hasRoles);
	const canEditPaymentMethod = hasRoles([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager]);

	const cardsQ = useQuery({
		queryKey: ['payment-cards'],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<PaymentCardDto[]>>(
				ENDPOINTS['/api/payment-cards/GetList'], {});
			return result.data.Data;
		},
	});

	const formatCardNumber = (last4?: string) => {
		return `•••• •••• •••• ${last4 || '****'}`;
	};

	const formatExpiry = (month?: number, year?: number) => {
		if (!month || !year) return 'N/A';
		const paddedMonth = month.toString().padStart(2, '0');
		const shortYear = year.toString().slice(-2);
		return `${paddedMonth}/${shortYear}`;
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return 'N/A';
		return moment(dateString).format('DD MMM YYYY, hh:mmA');
	};

	const data: PaymentMethod[] = cardsQ.data?.map((card) => ({
		id: card.Id,
		name: card.CardType || 'Card',
		paymentMethod: formatCardNumber(card.Last4),
		expiration: formatExpiry(card.ExpMonth, card.ExpYear),
		lastUsed: formatDate(card.UpdatedAt),
		isDefault: card.IsDefault,
	})) || [];
	const visibleColumns = canEditPaymentMethod ? columns : columns.filter((col) => col.field !== 'action');

	if (cardsQ.isLoading) {
		return <p className="text-neutral17 my-4">Loading payment methods...</p>;
	}

	if (cardsQ.isError) {
		return <p className="text-red-500 my-4">Error loading payment methods.</p>;
	}
	return (
		// todo styles
		<DataTable
			value={data}
			className={styles.customDatatable}
			tableStyle={{ minWidth: '50rem', width: '100%' }}
			footer={
				<TextTableHeader
					className="text-neutral17"
					text={`In total there ${data.length === 1 ? 'is' : 'are'} ${data.length} payment account ${data.length === 1 ? '' : 's'} registered`}
				/>
			}
			pt={{
				footer: { className: 'bg-surfaceContainerLow px-3 py-4 border-y border-[#E5E7EB]' }
			}}
		>
			{visibleColumns.map((col) => (
				<Column
					key={String(col.field)}
					field={
						col.field === 'action'
							? undefined
							: (col.field as string)
					}
					header={col.header}
					sortable={false}
					body={(rowData: PaymentMethod) => {
						if (col.field === 'action') {
							return (
								<button
									type="button"
									onClick={async () => {
										await showTypedModal2(RequestEditPaymentFormDialog, {
											cardId: rowData.id,
											onCardUpdated: () => cardsQ.refetch(),
										});
									}}
									className={styles.actionButtonContainer}
									aria-label="Edit payment method"
								>
									<MdOutlineCreate
										className="text-secondary"
										size={24}
									/>
								</button>
							);
						}

						const cellValue = rowData[col.field as keyof PaymentMethod];

						if (col.field === 'name') {
							return (
								<div className="flex items-center">
									<span>{cellValue}</span>
									{rowData.isDefault && (
										<Chip
											label="Default"
											className={styles.defaultChip}
										/>
									)}
								</div>
							);
						}

						if (col.field === 'paymentMethod') {
							const cardType = rowData.name.toLowerCase();
							const isVisa = cardType === 'visa';
							const cardIcon = isVisa ? '/images/svg/visa.svg' : '/images/svg/master.svg';
							const cardAlt = isVisa ? 'Visa' : 'Mastercard';

							return (
								<div className="flex items-center">
									<img
										src={cardIcon}
										alt={cardAlt}
										width={24}
										height={24}
										className="mr-[10px]"
									/>
									<span>{cellValue}</span>
								</div>
							);
						}

						return cellValue;
					}}
				/>
			))}
		</DataTable>
	);
};

export default PaymentMethodTable;
